import type { FC, ReactNode } from 'react'
import type { StaffStatus } from '@/types'

import { useMemo, useState } from 'react'
import { UserPlus, Users } from 'lucide-react'
import { Seo } from '@/components/generals/seo'
import { Layout } from '@/components/generals/layout'
import { Fab } from '@/components/app/fab'
import { DataGate } from '@/components/app/data-gate'
import { PageContainer, PageHeader } from '@/components/app/page-header'
import { Button } from '@/components/kit/button'
import { Segmented } from '@/components/kit/segmented'
import { EmptyState } from '@/components/kit/empty-state'
import { StaffCard } from '@/components/staff/staff-card'
import { StaffFormSheet } from '@/components/staff/staff-form-sheet'
import { AvailabilitySheet } from '@/components/staff/availability-sheet'
import { VehiclePickerSheet } from '@/components/staff/vehicle-picker-sheet'
import { useNow } from '@/hooks/use-now'
import { useCarWashData } from '@/hooks/use-carwash'
import { buildStaffBoard } from '@/lib/carwash/selectors'

type StaffFilter = 'all' | StaffStatus

const StaffView: FC = (): ReactNode => {
    const { state, lookups } = useCarWashData()
    const now = useNow(15_000)
    const board = useMemo(() => buildStaffBoard(state, now), [state, now])

    const [filter, setFilter] = useState<StaffFilter>('all')
    const [formOpen, setFormOpen] = useState(false)
    const [editingId, setEditingId] = useState<string | null>(null)
    const [availabilityId, setAvailabilityId] = useState<string | null>(null)
    const [availabilityOpen, setAvailabilityOpen] = useState(false)
    const [assignId, setAssignId] = useState<string | null>(null)
    const [assignOpen, setAssignOpen] = useState(false)

    const count = (status: StaffStatus) => board.filter(entry => entry.status === status).length
    const visible = filter === 'all' ? board : board.filter(entry => entry.status === filter)

    const editing = editingId ? lookups.staff.get(editingId) ?? null : null
    const availabilityEntry = availabilityId ? board.find(entry => entry.staff.id === availabilityId) ?? null : null
    const assignEntry = assignId ? board.find(entry => entry.staff.id === assignId) ?? null : null

    const openAdd = () => {
        setEditingId(null)
        setFormOpen(true)
    }

    return (
        <>
            <PageHeader
                title='Staff'
                subtitle={`${count('available')} available, ${count('busy')} busy${count('break') ? `, ${count('break')} on break` : ''}`}
                actions={(
                    <Button size='sm' onClick={openAdd} className='hidden sm:inline-flex'>
                        <UserPlus aria-hidden='true' />
                        Add staff
                    </Button>
                )}
            />

            <PageContainer className='space-y-4'>
                <Segmented
                    label='Filter staff'
                    value={filter}
                    onChange={setFilter}
                    options={[
                        { value: 'all', label: 'All', count: board.length },
                        { value: 'available', label: 'Available', count: count('available') },
                        { value: 'busy', label: 'Busy', count: count('busy') },
                        { value: 'break', label: 'Break', count: count('break') },
                        { value: 'inactive', label: 'Inactive', count: count('inactive') }
                    ]}
                />

                {visible.length === 0
                    ? (
                        <EmptyState
                            icon={<Users />}
                            title={board.length ? 'Nobody here right now' : 'Add your team'}
                            description={board.length ? 'Try another filter.' : 'Add the people who wash vehicles. They do not need an account or a login.'}
                            action={!board.length && (
                                <Button onClick={openAdd}>
                                    <UserPlus aria-hidden='true' />
                                    Add staff
                                </Button>
                            )}
                        />
                    )
                    : (
                        <div className='grid gap-3 md:grid-cols-2 xl:grid-cols-3'>
                            {visible.map(entry => (
                                <StaffCard
                                    key={entry.staff.id}
                                    entry={entry}
                                    onAssign={() => {
                                        setAssignId(entry.staff.id)
                                        setAssignOpen(true)
                                    }}
                                    onAvailability={() => {
                                        setAvailabilityId(entry.staff.id)
                                        setAvailabilityOpen(true)
                                    }}
                                    onEdit={() => {
                                        setEditingId(entry.staff.id)
                                        setFormOpen(true)
                                    }}
                                />
                            ))}
                        </div>
                    )}
            </PageContainer>

            <Fab label='Add staff' icon={<UserPlus aria-hidden='true' />} onClick={openAdd} className='sm:hidden' />

            <StaffFormSheet staff={editing} open={formOpen} onClose={() => setFormOpen(false)} />
            <AvailabilitySheet entry={availabilityEntry} open={availabilityOpen} onClose={() => setAvailabilityOpen(false)} />
            <VehiclePickerSheet
                staff={assignEntry?.staff ?? null}
                busy={assignEntry?.status === 'busy'}
                open={assignOpen}
                onClose={() => setAssignOpen(false)}
            />
        </>
    )
}

const StaffPage: FC = (): ReactNode => {
    return (
        <Layout>
            <Seo title='Staff' description='See who is free, who is busy and what each person is working on.' />

            <DataGate>
                <StaffView />
            </DataGate>
        </Layout>
    )
}

export default StaffPage
